// src/components/Layout.tsx
import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSession } from "../store/session";
import { useI18n } from "../i18n";
import { useTheme } from "../theme/ThemeProvider";

type NavItem = {
  to: string;
  key: string;
  icon: "home" | "compass" | "book" | "chat" | "settings" | "more";
};
const navItems = [
  { to: "/", key: "nav.home" },
  { to: "/quests", key: "nav.quests" },
  { to: "/calendar", key: "nav.calendar" }, // NEW
  { to: "/partners", key: "nav.partners" },
  { to: "/stories", key: "nav.stories" },
  { to: "/messages", key: "nav.messages" },
  { to: "/settings", key: "nav.settings" },
];

const desktopNav: NavItem[] = [
  { to: "/", key: "nav.home", icon: "home" },
  { to: "/quests", key: "nav.quests", icon: "compass" },
  { to: "/partners", key: "nav.partners", icon: "more" },
  { to: "/places", key: "nav.places", icon: "more" },
  { to: "/stories", key: "nav.stories", icon: "book" },
  { to: "/messages", key: "nav.messages", icon: "chat" },
  { to: "/consents", key: "nav.consents", icon: "more" },
  { to: "/settings", key: "nav.settings", icon: "settings" },
];

// Mobile: keep it close to app’s 5-tab mental model (extra pages still exist; reachable via desktop or internal links).
const mobileTabs: NavItem[] = [
  { to: "/", key: "nav.home", icon: "home" },
  { to: "/quests", key: "nav.quests", icon: "compass" },
  { to: "/stories", key: "nav.stories", icon: "book" },
  { to: "/messages", key: "nav.messages", icon: "chat" },
  { to: "/settings", key: "nav.settings", icon: "settings" },
];

function Icon({ name, active }: { name: NavItem["icon"]; active: boolean }) {
  const stroke = active ? "var(--primary2)" : "var(--muted)";
  const common = { width: 20, height: 20, fill: "none", stroke, strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

  switch (name) {
    case "home":
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M6.5 10.5V21h11V10.5" />
        </svg>
      );
    case "compass":
      return (
        <svg {...common} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" />
          <path d="M14.5 9.5 10 14l-1.5 5 5-1.5L18 13l-3.5-3.5Z" />
        </svg>
      );
    case "book":
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 0-3 3V4Z" />
          <path d="M8 20h11" />
        </svg>
      );
    case "chat":
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path d="M21 14a6 6 0 0 1-6 6H8l-5 3V8a6 6 0 0 1 6-6h6a6 6 0 0 1 6 6v6Z" />
        </svg>
      );
    case "settings":
    default:
      return (
        <svg {...common} viewBox="0 0 24 24">
          <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
          <path d="M19.4 15a7.8 7.8 0 0 0 .1-2l2-1.5-2-3.5-2.4 1a8 8 0 0 0-1.7-1l-.3-2.6H11l-.3 2.6a8 8 0 0 0-1.7 1l-2.4-1-2 3.5 2 1.5a7.8 7.8 0 0 0 .1 2l-2 1.5 2 3.5 2.4-1a8 8 0 0 0 1.7 1l.3 2.6h4l.3-2.6a8 8 0 0 0 1.7-1l2.4 1 2-3.5-2-1.5Z" />
        </svg>
      );
  }
}

export default function Layout() {
  const { user, logout } = useSession();
  const location = useLocation();
  const { locale, setLocale, t } = useI18n();
  const { mode, preset, toggleMode, setPreset } = useTheme();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const identityLine = useMemo(() => {
    const anyUser: any = user as any;
    const dn = anyUser?.displayName ?? anyUser?.display_name ?? null;
    const hint = anyUser?.emailHint ?? anyUser?.emailMasked ?? null;

    let localHint: string | null = null;
    try {
      localHint = window.localStorage.getItem("is_email_hint");
    } catch {}

    return dn || hint || localHint || t("common.guest");
  }, [user, t]);

  const shell =
    "min-h-screen flex flex-col " +
    "bg-[color:var(--bg)] text-[color:var(--text)]";

  return (
    <div className={shell}>
      {/* Header: keep it compact; desktop keeps full nav */}
      <header className="border-b border-[color:var(--border)] bg-[color:var(--bg)]/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 min-w-0">
          <img
            src="/brand/intimsync-mark.png"
            alt="IntimSync"
            className="w-12 h-12 object-contain flex-none"
            style={{
            display: "block",
              filter:
                "drop-shadow(0 0 6px rgba(236, 63, 115, 0.75)) drop-shadow(0 0 14px rgba(168, 85, 247, 0.55)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.35))",
            }}
          />
            <div className="flex flex-col leading-tight min-w-0">
              <span className="font-semibold text-sm tracking-wide">{t("common.appName")}</span>
              <span className="text-[11px] text-[color:var(--muted)] truncate max-w-[220px] sm:max-w-[320px]">
                {identityLine}
              </span>
            </div>
          </div>

          {/* Desktop pill navigation (existing behaviour) */}
          <nav className="hidden sm:flex flex-1 items-center gap-2 ml-4 overflow-x-auto min-w-0">
            {desktopNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    "px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors border",
                    isActive
                      ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]"
                      : "border-[color:var(--border)] text-[color:var(--muted)] hover:bg-[color:var(--surface2)] hover:text-[color:var(--text)]",
                  ].join(" ")
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
          </nav>

          {/* Right controls */}
          <div className="flex items-center gap-2 text-xs ml-auto">
            {/* Theme toggle */}
            <button
              type="button"
              onClick={toggleMode}
              className="px-3 py-1 rounded-full border text-[11px] transition-colors"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--surface2)",
                color: "var(--text)",
              }}
              title={`Theme: ${mode}`}
            >
              {mode}
            </button>

            {/* Preset toggle */}
            <button
              type="button"
              onClick={() => setPreset(preset === "romantic" ? "mature" : "romantic")}
              className="px-3 py-1 rounded-full border text-[11px] transition-colors"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--surface2)",
                color: "var(--text)",
              }}
              title={`Preset: ${preset}`}
            >
              {preset}
            </button>

            {/* Locale */}
            <button
              type="button"
              onClick={() => setLocale("en")}
              className="px-2 py-1 rounded-full border text-[11px]"
              style={{
                borderColor: locale === "en" ? "var(--primary)" : "var(--border)",
                color: locale === "en" ? "var(--primary2)" : "var(--muted)",
                backgroundColor: locale === "en" ? "color-mix(in srgb, var(--primary) 12%, transparent)" : "transparent",
              }}
            >
              EN
            </button>
            <button
              type="button"
              onClick={() => setLocale("nl")}
              className="px-2 py-1 rounded-full border text-[11px]"
              style={{
                borderColor: locale === "nl" ? "var(--primary)" : "var(--border)",
                color: locale === "nl" ? "var(--primary2)" : "var(--muted)",
                backgroundColor: locale === "nl" ? "color-mix(in srgb, var(--primary) 12%, transparent)" : "transparent",
              }}
            >
              NL
            </button>

            {user && (
              <button
                onClick={() => logout()}
                className="hidden sm:inline text-[11px] border rounded-full px-3 py-1 transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--muted)",
                }}
              >
                {t("common.logout")}
              </button>
            )}

            {/* Mobile “more” */}
            <button
              className="sm:hidden px-3 py-1 rounded-full border text-[11px]"
              style={{ borderColor: "var(--border)", color: "var(--text)", backgroundColor: "var(--surface2)" }}
              onClick={() => setMobileMenuOpen((v) => !v)}
            >
              Menu
            </button>
          </div>
        </div>

        {/* Mobile “menu sheet” for non-tab pages */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-[color:var(--border)] bg-[color:var(--bg)]">
            <div className="max-w-5xl mx-auto px-4 py-3 grid grid-cols-2 gap-2">
              {desktopNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    [
                      "px-3 py-2 rounded-xl border text-sm transition-colors",
                      isActive
                        ? "border-[color:var(--primary)] bg-[color:var(--surface2)]"
                        : "border-[color:var(--border)] hover:bg-[color:var(--surface2)]",
                    ].join(" ")
                  }
                  style={{ color: "var(--text)" }}
                >
                  {t(item.key)}
                </NavLink>
              ))}
              {user && (
                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: "var(--border)", color: "var(--text)", backgroundColor: "var(--surface2)" }}
                >
                  {t("common.logout")}
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Content. Add bottom padding for the fixed tab bar on mobile. */}
      <main className="flex-1">
        <div className="w-full max-w-5xl mx-auto px-2 sm:px-4 py-4 sm:py-6 pb-24 sm:pb-6 overflow-x-hidden">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tabs */}
      <nav
        className="sm:hidden fixed bottom-0 inset-x-0 border-t"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "color-mix(in srgb, var(--bg) 86%, transparent)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="max-w-5xl mx-auto px-2 py-2 grid grid-cols-5 gap-1">
          {mobileTabs.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) =>
                [
                  "rounded-2xl px-2 py-2 flex flex-col items-center justify-center gap-1 transition-colors border",
                  isActive
                    ? "border-[color:var(--primary)] bg-[color:var(--surface2)]"
                    : "border-transparent",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <>
                  <Icon name={it.icon} active={isActive} />
                  <span
                    className="text-[10px] font-semibold"
                    style={{ color: isActive ? "var(--primary2)" : "var(--muted)" }}
                  >
                    {t(it.key)}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Footer stays desktop-only (optional; the app doesn’t show path). */}
      <footer className="hidden sm:block border-t py-3 px-4 text-[11px]" style={{ borderColor: "var(--border)", color: "var(--muted)" }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} IntimSync</span>
          <span className="truncate hidden sm:inline">{location.pathname}</span>
        </div>
      </footer>
    </div>
  );
}
